import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Plus, Music2, Trash2, ExternalLink, Users, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PlaylistsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", collaborative: true });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/playlists").then((r) => setPlaylists(r.data.playlists)).finally(() => setLoading(false));
  }, [api]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/playlists", form);
      setPlaylists((prev) => [res.data.playlist, ...prev]);
      setShowCreate(false);
      setForm({ title: "", description: "", collaborative: true });
    } catch (err) { console.error(err); }
    setCreating(false);
  };

  const platformColor = () => "#FF0000";

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground" data-testid="playlists-title">Playlists</h1>
          <p className="text-sm text-muted-foreground">Creez et partagez des playlists collaboratives</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-[#FF6B35] text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-1.5 hover:scale-105 active:scale-95 transition-transform" data-testid="create-playlist-btn">
          <Plus className="w-4 h-4" /> Nouvelle
        </button>
      </div>

      {/* Create modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowCreate(false)}>
            <motion.form initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} onClick={(e) => e.stopPropagation()} onSubmit={handleCreate} className="bg-card rounded-2xl p-6 w-96 max-w-[90vw] shadow-2xl space-y-4" data-testid="create-playlist-form">
              <h3 className="font-heading font-bold text-lg text-card-foreground">Nouvelle playlist</h3>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Nom de la playlist" className="w-full bg-white border border-border rounded-xl px-3 py-2 text-sm text-card-foreground focus:border-[#FF6B35] outline-none" data-testid="playlist-title-input" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optionnel)" rows={2} className="w-full bg-white border border-border rounded-xl px-3 py-2 text-sm text-card-foreground focus:border-[#FF6B35] outline-none resize-none" />
              <label className="flex items-center gap-2 text-sm text-card-foreground/80">
                <input type="checkbox" checked={form.collaborative} onChange={(e) => setForm({ ...form, collaborative: e.target.checked })} className="accent-[#FF6B35] w-4 h-4" />
                Collaborative (tout le monde peut ajouter des titres)
              </label>
              <button type="submit" disabled={creating || !form.title.trim()} className="w-full bg-[#FF6B35] text-white py-2.5 rounded-full text-sm font-bold hover:bg-[#E85A25] transition-colors disabled:opacity-50" data-testid="playlist-create-submit">
                {creating ? "..." : "Creer"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Playlists list */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
      ) : playlists.length === 0 ? (
        <div className="text-center py-12">
          <Music2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">Aucune playlist pour le moment</p>
          <p className="text-muted-foreground text-xs mt-1">Creez-en une pour commencer !</p>
        </div>
      ) : (
        <div className="space-y-3">
          {playlists.map((pl) => (
            <button key={pl.id} onClick={() => navigate(`/playlists/${pl.id}`)} className="w-full text-left bg-popover/60 border border-foreground/5 rounded-2xl p-4 hover:border-[#FF6B35]/30 transition-colors group" data-testid={`playlist-item-${pl.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FF8FA3] flex items-center justify-center shrink-0">
                  <Music2 className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-foreground truncate">{pl.title}</p>
                  {pl.description && <p className="text-xs text-muted-foreground truncate mt-0.5">{pl.description}</p>}
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-muted-foreground">{pl.tracks?.length || 0} titres</span>
                    {pl.collaborative && <span className="text-[10px] text-[#FF6B35] flex items-center gap-0.5"><Users className="w-3 h-3" /> Collaborative</span>}
                    <span className="text-[10px] text-muted-foreground">par {pl.creator_pseudo}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[#FF6B35] transition-colors shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
